import React from 'react';
import { Box, Chip, CircularProgress, Typography } from '@mui/material';
import { colors } from '../theme/designSystem';
import { FileViewers, FILE_VISIBILITIES } from '../services/patientFilesService';

/**
 * "Quem abre hoje": a regra de Quem pode ver resolvida em nomes (RN-538d). No modal de quem
 * gerencia acompanha a regra escolhida antes de salvar; para os demais é o modal inteiro —
 * quem não abre o arquivo descobre a quem pedir acesso.
 */
interface Props {
  viewers: FileViewers | null;
  loading: boolean;
}

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <Box sx={{ mb: 1.5 }}>
    <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: colors.textSecondary, textTransform: 'uppercase', letterSpacing: 0.3, mb: 0.5 }}>
      {title}
    </Typography>
    {children}
  </Box>
);

const Names: React.FC<{ items: { id: string; name: string; detail?: string | null; patientWide?: boolean }[]; empty: string }> = ({ items, empty }) => (
  items.length === 0
    ? <Typography sx={{ fontSize: '0.85rem', color: colors.textSecondary }}>{empty}</Typography>
    : (
      <Box component="ul" sx={{ m: 0, pl: 2.5, display: 'flex', flexDirection: 'column', gap: 0.25 }}>
        {items.map(i => (
          <Typography component="li" key={i.id} sx={{ fontSize: '0.875rem', color: colors.text, overflowWrap: 'anywhere' }}>
            {i.name}
            {i.detail && <Box component="span" sx={{ color: colors.textSecondary }}> · {i.detail}</Box>}
            {i.patientWide && (
              <Chip label="todos os arquivos do paciente" size="small" sx={{ ml: 1, height: 18, fontSize: '0.68rem' }} />
            )}
          </Typography>
        ))}
      </Box>
    )
);

const PatientFileViewersList: React.FC<Props> = ({ viewers, loading }) => {
  if (loading && !viewers) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
        <CircularProgress size={22} sx={{ color: colors.primary }} />
      </Box>
    );
  }
  if (!viewers) return null;

  const rule = FILE_VISIBILITIES.find(v => v.value === viewers.visibility);
  const fileProfiles = viewers.profiles.filter(p => !p.patientWide);
  const fileUsers = viewers.users.filter(u => !u.patientWide);
  const patientWide = [...viewers.profiles.filter(p => p.patientWide), ...viewers.users.filter(u => u.patientWide)];

  return (
    <Box sx={{ border: `1px solid ${colors.backgroundAlt}`, borderRadius: '8px', p: 2, opacity: loading ? 0.6 : 1, transition: 'opacity 120ms' }}>
      <Typography sx={{ fontSize: '0.9rem', fontWeight: 600, color: colors.text, mb: 0.25 }}>Quem abre hoje</Typography>
      <Typography sx={{ fontSize: '0.8rem', color: colors.textSecondary, mb: 1.5 }}>
        {rule?.label}{rule ? ': ' : ''}{rule?.help}
      </Typography>

      <Section title="Sempre">
        <Names
          items={[
            { id: 'author', name: viewers.authorName ? `${viewers.authorName} (enviou o arquivo)` : 'Quem enviou o arquivo' },
            { id: 'admin', name: 'Administrador' },
          ]}
          empty=""
        />
      </Section>

      {viewers.visibility === 'team' && (
        <Section title="Equipe profissional do paciente">
          <Names items={viewers.careTeam} empty="Nenhum profissional atende este paciente ainda." />
        </Section>
      )}

      {viewers.visibility === 'all' && (
        <Section title="Todos">
          <Typography sx={{ fontSize: '0.875rem', color: colors.text }}>Todos que têm acesso a este paciente, inclusive a recepção.</Typography>
        </Section>
      )}

      {viewers.visibility === 'custom' && (
        <>
          <Typography sx={{ fontSize: '0.8rem', color: colors.textSecondary, mb: 1.5 }}>
            As duas listas se somam: abre quem estiver em um dos perfis liberados <strong>ou</strong> for um dos usuários liberados.
            Pelo perfil, só quem tem acesso a este paciente.
          </Typography>
          <Section title="Perfis liberados">
            <Names items={fileProfiles} empty="Nenhum perfil." />
          </Section>
          <Section title="Usuários liberados">
            <Names items={fileUsers} empty="Nenhum usuário." />
          </Section>
        </>
      )}

      {patientWide.length > 0 && (
        <Section title="Liberados para todos os arquivos deste paciente">
          <Names items={patientWide.map(p => ({ ...p, patientWide: false }))} empty="" />
        </Section>
      )}
    </Box>
  );
};

export default PatientFileViewersList;
